import React, { useRef, useState } from "react";
import { FiUploadCloud, FiTrash2, FiMessageSquare } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { setModalOpen } from "../../store/modalSlice";
import ModalBase from "../Modal/ModalBase";
import { Field } from "../ReusableComponents/Select";
import { ErrorLogService } from "../../services/errorLogService";
import { uploadErrorLogImages } from "../../utils/errorLogUpload";

interface ReportErrorModalProps {
  /** Provided by ModalGlobal — closes the modal. */
  onClose?: (result?: any) => void;
  /** Conversation the user was in when reporting — stored as mcpConversationId (nullable). */
  conversationId?: string | null;
  /** Reporter email, denormalized onto the record for follow-up. */
  userEmail?: string | null;
  /** Optional callback fired after a successful submission. */
  onSubmitted?: () => void;
}

const ReportErrorModal: React.FC<ReportErrorModalProps> = ({
  onClose,
  conversationId,
  userEmail,
  onSubmitted,
}) => {
  const dispatch = useDispatch();
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const closeModal = () => {
    // Revoke object URLs to avoid leaks
    previews.forEach((url) => URL.revokeObjectURL(url));
    if (onClose) onClose();
    else dispatch(setModalOpen(false));
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (selected.length === 0) return;
    setFiles((prev) => [...prev, ...selected]);
    setPreviews((prev) => [
      ...prev,
      ...selected.map((f) => URL.createObjectURL(f)),
    ]);
    // Allow re-selecting the same file later
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Please add your feedback before submitting.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      let id: string | undefined;
      let cognitoIdentityId: string | undefined;
      let keys: string[] = [];

      if (files.length > 0) {
        const uploaded = await uploadErrorLogImages(files);
        id = uploaded.errorLogId;
        cognitoIdentityId = uploaded.identityId;
        keys = uploaded.keys;
      }

      await ErrorLogService.createErrorLog({
        id,
        description: description.trim(),
        images: keys,
        cognitoIdentityId,
        mcpConversationId: conversationId ?? null,
        source: "USER_REPORT",
        // Origin + path only — never capture query params or hash, which can contain sensitive state (tokens, search terms, share links).
        pageUrl: window.location.origin + window.location.pathname,
        userEmail: userEmail ?? null,
      });

      setSuccess(true);
      onSubmitted?.();
      // Auto-close shortly after success
      setTimeout(closeModal, 1200);
    } catch (err) {
      console.error("Failed to submit error report:", err);
      setError("Something went wrong submitting your feedback. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBase
      title="Provide Feedback"
      icon={<FiMessageSquare className="h-5 w-5" />}
      onClose={closeModal}
      size="md"
      footer={
        success ? undefined : (
          <>
            <button
              type="button"
              onClick={closeModal}
              className="eai-btn-outline"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="eai-btn-cta"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting…" : "Submit feedback"}
            </button>
          </>
        )
      }
    >
      {success ? (
        <div className="py-8 text-center">
          <p className="eai-panel-title">Thanks for your feedback!</p>
          <p className="eai-description-sm mt-1">Our team will take a look.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            <Field label="Feedback" htmlFor="feedback-description">
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Tell us what you liked, what didn't work, or anything you'd like to see improved…"
                className="eai-input resize-none"
                disabled={isSubmitting}
              />
            </Field>

            {/* Image picker */}
            <Field label="Screenshots (optional)">
              <div className="flex flex-wrap gap-2">
                {previews.map((src, i) => (
                  <div key={src} className="eai-media relative h-20 w-20">
                    <img
                      src={src}
                      alt={`Screenshot ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute right-1 top-1 rounded-full bg-eai-surface-card/85 p-1 text-eai-ink-muted transition-colors hover:bg-eai-surface-card hover:text-eai-danger-text active:bg-eai-surface-active"
                      aria-label="Remove image"
                      disabled={isSubmitting}
                    >
                      <FiTrash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-eai-card border border-dashed border-eai-border-hover bg-eai-surface-control text-eai-ink-faint transition-colors hover:border-eai-border-active hover:bg-eai-surface-hover hover:text-eai-ink-soft active:bg-eai-surface-active disabled:pointer-events-none disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  <FiUploadCloud className="h-5 w-5" />
                  <span className="font-outfit text-eai-caption">Add</span>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                className="hidden"
              />
            </Field>
          </div>

          {error && (
            <div className="mt-4 rounded-eai-card border border-eai-danger-border bg-eai-danger-surface px-3 py-2 font-inter text-eai-body-sm text-eai-danger-text">
              {error}
            </div>
          )}
        </>
      )}
    </ModalBase>
  );
};

export default ReportErrorModal;
